// ============================================================================
// QrShareModal.jsx — QR code bridge for Instagram/TikTok on desktop
// User scans QR with phone → image opens on phone → saves to camera roll
// ============================================================================

import { useEffect } from 'react'

export default function QrShareModal({ platform, imageUrl, caption, shareUrl, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  if (!platform) return null

  const platformName = platform === 'instagram' ? 'Instagram' : 'TikTok'
  const platformIcon = platform === 'instagram' ? '📸' : '🎵'

  // Use Google Charts QR API (free, no auth) — embeds the image URL
  // so scanning with phone camera opens the image, user long-presses to save
  const qrTarget = imageUrl
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(qrTarget)}&margin=0&color=1e293b&bgcolor=ffffff`

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-md w-full border-t sm:border border-slate-200 dark:border-slate-700 overflow-hidden mb-safe"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{platformIcon}</span>
            <h2 className="text-base font-bold text-slate-900 dark:text-white">
              Share to {platformName}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 w-8 h-8 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-center"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          <div className="bg-gradient-to-br from-zenna-violet/5 to-zenna-cyan/5 rounded-xl p-4 mb-4">
            <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-2">
              ✓ Image downloaded to your computer
            </p>
            <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">
              ✓ Caption copied to clipboard
            </p>
          </div>

          <div className="text-center mb-4">
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-3 font-semibold uppercase tracking-wider">
              Or scan with your phone
            </div>
            <div className="inline-block p-3 bg-white rounded-xl border border-slate-200">
              <img
                src={qrSrc}
                alt="QR code to image"
                className="w-48 h-48"
                loading="lazy"
              />
            </div>
          </div>

          <div className="text-xs text-slate-600 dark:text-slate-400 space-y-2 bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg">
            <div className="font-semibold text-slate-700 dark:text-slate-300 mb-1">Next steps:</div>
            <ol className="list-decimal list-inside space-y-1 pl-1">
              <li>Scan the QR code with your phone camera (or use the downloaded image)</li>
              <li>Long-press the image to save it to your camera roll</li>
              <li>Open {platformName} and create a new post</li>
              <li>Paste the caption we copied for you</li>
            </ol>
          </div>

          {/* Caption preview */}
          <div className="mt-4">
            <div className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Caption on clipboard:</div>
            <div className="text-xs text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-900 rounded-lg p-3 max-h-20 overflow-y-auto">
              {caption}
            </div>
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(caption)
                  alert('📋 Caption copied again!')
                } catch { /* ignore */ }
              }}
              className="mt-2 text-xs text-zenna-violet hover:underline"
            >
              Copy caption again
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end bg-slate-50 dark:bg-slate-900/50">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white text-sm font-semibold hover:opacity-90 transition min-h-[44px]"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  )
}
